import { useCallback, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ZodIssue } from 'zod';
import { Button, Input, Modal, Checkbox } from '@immo/ui-kit';
import { CreateAdminWorkspaceBodySchema } from '@immo/api-contract';
import { AddressAutocomplete } from '@immo/components';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { env } from '@/lib/env';
import { FormField } from '@/components/form';
import type { ParsedAddress } from '@immo/components';

type Field =
  | 'name'
  | 'ownerEmail'
  | 'ownerFirstName'
  | 'ownerLastName'
  | 'street'
  | 'houseNumber'
  | 'postalCode'
  | 'city'
  | 'country';

type FormErrors = Readonly<Record<Field, string | null>>;

const NO_ERRORS: FormErrors = {
  name: null,
  ownerEmail: null,
  ownerFirstName: null,
  ownerLastName: null,
  street: null,
  houseNumber: null,
  postalCode: null,
  city: null,
  country: null,
};

function fieldFromPath(path: ReadonlyArray<PropertyKey>): Field | undefined {
  const [head, sub] = path;

  if (head === 'name') return 'name';

  if (head === 'owner') {
    if (sub === 'email') return 'ownerEmail';

    if (sub === 'firstName') return 'ownerFirstName';

    if (sub === 'lastName') return 'ownerLastName';
  }

  if (head === 'address') {
    if (sub === 'street') return 'street';

    if (sub === 'houseNumber') return 'houseNumber';

    if (sub === 'postalCode') return 'postalCode';

    if (sub === 'city') return 'city';

    if (sub === 'country') return 'country';
  }

  return undefined;
}

function issueKey(field: Field, issue: ZodIssue, rawInput: string): string {
  if (field === 'name') {
    if (issue.code === 'too_small') return rawInput.trim().length === 0 ? 'required' : 'too-short';

    if (issue.code === 'too_big') return 'too-long';
  }

  if (field === 'ownerEmail') {
    return rawInput.trim().length === 0 ? 'required' : 'invalid';
  }

  if (field === 'postalCode') {
    return rawInput.trim().length === 0 ? 'required' : 'invalid';
  }

  if (issue.code === 'too_small') return 'required';

  if (issue.code === 'too_big') return 'too-long';

  return 'invalid';
}

export function NewWorkspacePage() {
  const t = useTranslations('pages.admin.workspaces.create');
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [ownerLastName, setOwnerLastName] = useState('');
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Deutschland');
  const [isTest, setIsTest] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const clearError = (key: Field) => setErrors((prev) => (prev[key] === null ? prev : { ...prev, [key]: null }));

  const applyAutocomplete = useCallback((address: ParsedAddress) => {
    setStreet(address.street);
    setHouseNumber(address.houseNumber);
    setPostalCode(address.postalCode);
    setCity(address.city);
    setLatitude(address.latitude);
    setLongitude(address.longitude);

    if (address.country !== '') {
      setCountry(address.country);
    }

    setErrors(NO_ERRORS);
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenericError(null);

    const rawByField: Readonly<Record<Field, string>> = {
      name,
      ownerEmail,
      ownerFirstName,
      ownerLastName,
      street,
      houseNumber,
      postalCode,
      city,
      country,
    };

    const parsed = CreateAdminWorkspaceBodySchema.safeParse({
      name,
      isTest,
      owner: { email: ownerEmail, firstName: ownerFirstName, lastName: ownerLastName },
      address: { street, houseNumber, postalCode, city, country, latitude, longitude },
    });

    if (!parsed.success) {
      const next: Record<Field, string | null> = { ...NO_ERRORS };

      for (const issue of parsed.error.issues) {
        const field = fieldFromPath(issue.path);

        if (field !== undefined && next[field] === null) {
          next[field] = t(`errors.${labelKey(field)}.${issueKey(field, issue, rawByField[field])}`);
        }
      }

      setErrors(next);

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminWorkspaces.create({ body: parsed.data });

      if (result.ok) {
        // The detail page tells the operator a freshly created test workspace is
        // hidden from the lists while the filter is inactive (IMM-593.2) — the
        // one moment the default reliably surprises someone. Navigation state,
        // not a URL flag: the note is for this arrival only, never for a reload.
        navigate(`/workspaces/${result.body.id}`, { state: { createdAsTest: parsed.data.isTest === true } });

        return;
      }

      setGenericError(t(result.status === 403 ? 'forbidden-error' : 'generic-error'));
    } catch {
      setGenericError(t('generic-error'));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => navigate({ pathname: '/workspaces', search: location.search });

  // Compare against the form's INITIAL values, not against empty: `country`
  // defaults to 'Deutschland' and `isTest` to false, so a `!== ''` sweep would
  // report dirty on an untouched form and keep raising the discard prompt over
  // a form nothing was typed into — the very defect this fixes (IMM-856.1).
  const dirty =
    name !== '' ||
    ownerEmail !== '' ||
    ownerFirstName !== '' ||
    ownerLastName !== '' ||
    street !== '' ||
    houseNumber !== '' ||
    postalCode !== '' ||
    city !== '' ||
    country !== 'Deutschland' ||
    isTest !== false;

  return (
    <Modal
      open
      onClose={close}
      dirty={dirty}
      size="lg"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
            {t('back-cta')}
          </Button>
          <Button type="submit" form="workspace-create-form" disabled={submitting}>
            {submitting ? t('submit-loading') : t('submit-cta')}
          </Button>
        </>
      }
    >
      <form id="workspace-create-form" className="space-y-4" onSubmit={submit} noValidate>
        <FormField id="workspace-name" label={t('name-label')} error={errors.name}>
          {(errId) => (
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearError('name');
              }}
              placeholder={t('name-placeholder')}
              disabled={submitting}
              invalid={errors.name !== null}
              aria-describedby={errId}
            />
          )}
        </FormField>

        <label className="flex items-center gap-2 text-sm text-text-primary">
          <Checkbox checked={isTest} disabled={submitting} onCheckedChange={(checked) => setIsTest(checked)} />
          {t('is-test-label')}
        </label>

        <FormField id="owner-email" label={t('owner-email-label')} error={errors.ownerEmail}>
          {(errId) => (
            <Input
              id="owner-email"
              type="email"
              value={ownerEmail}
              onChange={(e) => {
                setOwnerEmail(e.target.value);
                clearError('ownerEmail');
              }}
              placeholder={t('owner-email-placeholder')}
              disabled={submitting}
              invalid={errors.ownerEmail !== null}
              aria-describedby={errId}
            />
          )}
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="owner-first-name" label={t('owner-first-name-label')} error={errors.ownerFirstName}>
            {(errId) => (
              <Input
                id="owner-first-name"
                value={ownerFirstName}
                onChange={(e) => {
                  setOwnerFirstName(e.target.value);
                  clearError('ownerFirstName');
                }}
                placeholder={t('owner-first-name-placeholder')}
                disabled={submitting}
                invalid={errors.ownerFirstName !== null}
                aria-describedby={errId}
              />
            )}
          </FormField>
          <FormField id="owner-last-name" label={t('owner-last-name-label')} error={errors.ownerLastName}>
            {(errId) => (
              <Input
                id="owner-last-name"
                value={ownerLastName}
                onChange={(e) => {
                  setOwnerLastName(e.target.value);
                  clearError('ownerLastName');
                }}
                placeholder={t('owner-last-name-placeholder')}
                disabled={submitting}
                invalid={errors.ownerLastName !== null}
                aria-describedby={errId}
              />
            )}
          </FormField>
        </div>

        <hr className="border-border" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{t('address-heading')}</h2>

        <AddressAutocomplete
          apiKey={env.googleMapsApiKey}
          label={t('address-search-label')}
          placeholder={t('address-search-placeholder')}
          disabled={submitting}
          onPlaceSelected={applyAutocomplete}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_120px]">
          <FormField id="street" label={t('street-label')} error={errors.street}>
            {(errId) => (
              <Input
                id="street"
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                  clearError('street');
                }}
                placeholder={t('street-placeholder')}
                disabled={submitting}
                invalid={errors.street !== null}
                aria-describedby={errId}
              />
            )}
          </FormField>
          <FormField id="house-number" label={t('house-number-label')} error={errors.houseNumber}>
            {(errId) => (
              <Input
                id="house-number"
                value={houseNumber}
                onChange={(e) => {
                  setHouseNumber(e.target.value);
                  clearError('houseNumber');
                }}
                placeholder={t('house-number-placeholder')}
                disabled={submitting}
                invalid={errors.houseNumber !== null}
                aria-describedby={errId}
              />
            )}
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[120px_1fr]">
          <FormField id="postal-code" label={t('postal-code-label')} error={errors.postalCode}>
            {(errId) => (
              <Input
                id="postal-code"
                inputMode="numeric"
                value={postalCode}
                onChange={(e) => {
                  setPostalCode(e.target.value);
                  clearError('postalCode');
                }}
                placeholder={t('postal-code-placeholder')}
                disabled={submitting}
                invalid={errors.postalCode !== null}
                aria-describedby={errId}
              />
            )}
          </FormField>
          <FormField id="city" label={t('city-label')} error={errors.city}>
            {(errId) => (
              <Input
                id="city"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  clearError('city');
                }}
                placeholder={t('city-placeholder')}
                disabled={submitting}
                invalid={errors.city !== null}
                aria-describedby={errId}
              />
            )}
          </FormField>
        </div>

        <FormField id="country" label={t('country-label')} error={errors.country}>
          {(errId) => (
            <Input
              id="country"
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                clearError('country');
              }}
              placeholder={t('country-placeholder')}
              disabled={submitting}
              invalid={errors.country !== null}
              aria-describedby={errId}
            />
          )}
        </FormField>

        {genericError !== null && <p className="text-base text-danger-600">{genericError}</p>}
      </form>
    </Modal>
  );
}

function labelKey(field: Field): string {
  switch (field) {
    case 'ownerEmail':
      return 'owner-email';
    case 'ownerFirstName':
      return 'owner-first-name';
    case 'ownerLastName':
      return 'owner-last-name';
    case 'houseNumber':
      return 'house-number';
    case 'postalCode':
      return 'postal-code';
    default:
      return field;
  }
}
